import { useState, useEffect } from "react";
import { 
  Zap, 
  Copy, 
  Check, 
  X, 
  Search,
  Phone,
  Truck,
  ExternalLink,
  Loader2,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPreventivoVeloce({ user }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [officine, setOfficine] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, officineRes] = await Promise.all([
        authFetch(`${API}/admin/pv/stats`, { }),
        authFetch(`${API}/admin/pv/officine`, { })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (officineRes.ok) setOfficine(await officineRes.json());
    } catch (error) {
      toast.error("Errore caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async (userId) => {
    try {
      const res = await authFetch(`${API}/admin/pv/officine/${userId}/token`, {
        method: "POST"
      });

      if (res.ok) {
        const data = await res.json();
        setOfficine(prev => prev.map(o => 
          o.user_id === userId ? { ...o, pv_token: data.token } : o
        ));
        toast.success("Token generato con successo");
      } else {
        toast.error("Errore generazione token");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const toggleModule = async (userId, enabled) => {
    try {
      const res = await authFetch(`${API}/admin/pv/officine/${userId}/module`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled })
      });

      if (res.ok) {
        setOfficine(prev => prev.map(o => 
          o.user_id === userId ? { ...o, pv_enabled: enabled } : o
        ));
        toast.success(enabled ? "Modulo abilitato" : "Modulo disabilitato");
        // Refresh stats
        const statsRes = await authFetch(`${API}/admin/pv/stats`, { });
        if (statsRes.ok) setStats(await statsRes.json());
      } else {
        toast.error("Errore aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const copyToken = async (token) => {
    try {
      const fullUrl = `${window.location.origin}/r/${token}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedToken(token);
      toast.success("Link copiato!");
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      toast.error("Impossibile copiare");
    }
  };

  const filteredOfficine = officine.filter(o => 
    (o.company_name || o.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.email || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pv-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Richiesta Rapida Ricambi</h1>
            <p className="text-zinc-500 text-sm">Gestione modulo PWA per officine</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          className="border-zinc-700 text-zinc-400"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <p className="text-blue-400 text-sm">
          <strong>Come funziona:</strong> Il numero WhatsApp per l'invio delle richieste viene preso 
          dal primo fornitore configurato nell'officina. L'officina può gestire i propri fornitori 
          dalla sezione "Fornitori" nel menu laterale.
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-zinc-500 text-xs uppercase">Moduli Attivi</p>
            <p className="text-2xl font-bold text-green-500 mt-1">{stats.modules_enabled}</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-zinc-500 text-xs uppercase">Token Generati</p>
            <p className="text-2xl font-bold text-blue-500 mt-1">{stats.officine_with_token}</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-zinc-500 text-xs uppercase">Richieste Totali</p>
            <p className="text-2xl font-bold text-white mt-1">{stats.total_requests}</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-zinc-500 text-xs uppercase">Questo Mese</p>
            <p className="text-2xl font-bold text-primary mt-1">{stats.requests_this_month}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          placeholder="Cerca per nome o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:border-primary outline-none"
        />
      </div>

      {/* Officine List */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white">Officine ({filteredOfficine.length})</h2>
        
        {filteredOfficine.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            Nessuna officina trovata
          </div>
        ) : (
          filteredOfficine.map((officina) => (
            <div 
              key={officina.user_id}
              className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium truncate">
                      {officina.company_name || officina.name}
                    </h3>
                    {officina.pv_enabled && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-500 text-xs rounded-full">
                        Attivo
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-sm truncate">{officina.email}</p>
                  
                  {/* Supplier Info */}
                  {officina.has_supplier_with_phone ? (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-400">
                        {officina.supplier_name || "Fornitore"}
                      </span>
                      <span className="text-green-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {officina.supplier_phone}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center gap-2 text-sm text-amber-500">
                      <AlertCircle className="w-4 h-4" />
                      <span>Nessun fornitore con numero telefono</span>
                    </div>
                  )}
                  
                  {/* Token Display */}
                  {officina.pv_token && (
                    <div className="mt-2 flex items-center gap-2">
                      <code className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                        /r/{officina.pv_token.substring(0, 8)}...
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToken(officina.pv_token)}
                        className="h-6 px-2 text-zinc-400 hover:text-white"
                      >
                        {copiedToken === officina.pv_token ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/r/${officina.pv_token}`, "_blank")}
                        className="h-6 px-2 text-zinc-400 hover:text-white"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateToken(officina.user_id)}
                    className="border-zinc-700 text-zinc-300 text-xs"
                  >
                    {officina.pv_token ? "Rigenera Token" : "Genera Token"}
                  </Button>
                  
                  <Button
                    variant={officina.pv_enabled ? "destructive" : "default"}
                    size="sm"
                    onClick={() => toggleModule(officina.user_id, !officina.pv_enabled)}
                    disabled={!officina.pv_token || !officina.has_supplier_with_phone}
                    className={`text-xs ${officina.pv_enabled ? "" : "bg-green-600 hover:bg-green-700"}`}
                  >
                    {officina.pv_enabled ? "Disabilita" : "Abilita"}
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
